import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2, Loader2, AlertCircle, Check } from "lucide-react";
import { api, type AnimationDto } from "../lib/api";
import { Spinner } from "../components/Spinner";
import { cn } from "../lib/cn";

export function Dashboard() {
  const [items, setItems] = useState<AnimationDto[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function refresh() {
    try { const r = await api.listAnimations(); setItems(r.animations); }
    catch (e: any) { setErr(e?.message ?? "Failed to load"); }
  }

  useEffect(() => { refresh(); }, []);

  // Poll while any item is in-flight.
  useEffect(() => {
    if (!items) return;
    const anyInFlight = items.some((a) => ["analyzing", "planning", "rendering", "encoding", "queued"].includes(a.status));
    if (!anyInFlight) return;
    const t = setInterval(refresh, 1500);
    return () => clearInterval(t);
  }, [items]);

  async function remove(id: string) {
    if (!confirm("Delete this animation?")) return;
    await api.deleteAnim(id);
    setItems((cur) => cur?.filter((a) => a.id !== id) ?? []);
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
      <div className="mb-8 flex items-end justify-between">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-white">Your animations</h1>
          <p className="text-sm text-white/55">{items ? `${items.length} total` : "Loading…"}</p>
        </div>
        <Link to="/editor/new" className="btn-primary"><Plus className="h-4 w-4" /> New animation</Link>
      </div>

      {err && <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">{err}</div>}
      {!items && <div className="grid place-items-center py-20"><Spinner /></div>}

      {items && items.length === 0 && (
        <div className="card grid place-items-center py-20 text-center">
          <p className="text-white/70">No animations yet.</p>
          <p className="mt-1 text-sm text-white/40">Upload a logo and write a prompt to make your first one.</p>
          <Link to="/editor/new" className="btn-primary mt-5"><Plus className="h-4 w-4" /> Get started</Link>
        </div>
      )}

      {items && items.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((a) => <Tile key={a.id} a={a} onDelete={() => remove(a.id)} />)}
        </div>
      )}
    </div>
  );
}

function Tile({ a, onDelete }: { a: AnimationDto; onDelete: () => void }) {
  return (
    <Link to={`/editor/${a.id}`} className="card card-hover relative block overflow-hidden">
      <div className="aspect-[4/3] w-full grid-bg">
        <div className="grid h-full place-items-center p-6 text-xs text-white/30 font-mono">
          {a.templateId ?? "—"}
        </div>
      </div>
      <div className="flex items-center justify-between gap-3 border-t border-white/[0.06] p-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm text-white">{a.prompt}</p>
          <div className="mt-1 flex items-center gap-2">
            <StatusBadge status={a.status} />
            <span className="font-mono text-[10px] text-white/30">{new Date(a.createdAt).toLocaleDateString()}</span>
          </div>
        </div>
        <button onClick={(e) => { e.preventDefault(); onDelete(); }} className="rounded-md p-1.5 text-white/40 hover:bg-white/[0.04] hover:text-white">
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </Link>
  );
}

function StatusBadge({ status }: { status: AnimationDto["status"] }) {
  const map = {
    queued:     { label: "Queued",    cls: "text-white/50", icon: <Loader2 className="h-3 w-3 spin" /> },
    analyzing:  { label: "Analyzing", cls: "text-white/70", icon: <Loader2 className="h-3 w-3 spin" /> },
    planning:   { label: "Planning",  cls: "text-white/70", icon: <Loader2 className="h-3 w-3 spin" /> },
    rendering:  { label: "Rendering", cls: "text-white/70", icon: <Loader2 className="h-3 w-3 spin" /> },
    encoding:   { label: "Encoding",  cls: "text-white/70", icon: <Loader2 className="h-3 w-3 spin" /> },
    ready:      { label: "Ready",     cls: "text-emerald-300", icon: <Check className="h-3 w-3" /> },
    failed:     { label: "Failed",    cls: "text-red-300",  icon: <AlertCircle className="h-3 w-3" /> },
    cancelled:  { label: "Cancelled", cls: "text-white/40", icon: <AlertCircle className="h-3 w-3" /> },
  } as const;
  const it = map[status];
  return <span className={cn("inline-flex items-center gap-1 text-[10px]", it.cls)}>{it.icon}{it.label}</span>;
}
